import type {
  ScreenInfo,
  BrowserInfo,
  TimezoneInfo,
  HardwareInfo,
  CanvasFingerprint,
  WebGLFingerprint,
  DeviceFingerprint,
  TrackingData,
} from "../types/attributionTracker.types";

const SESSION_ID_KEY = "attribution_session_id";

/**
 * 设备指纹收集和归因追踪器
 */
class AttributionTracker {
  private sessionId: string;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
  }

  /**
   * 收集完整的设备指纹
   */
  collectDeviceFingerprint(): DeviceFingerprint {
    return {
      screen: this.collectScreenInfo(),
      browser: this.collectBrowserInfo(),
      timezone: this.collectTimezoneInfo(),
      hardware: this.collectHardwareInfo(),
      stableId: this.generateStableFingerprintId(),
      webglInfo: this.collectWebGLFingerprint(),
      canvasInfo: this.collectCanvasFingerprint(),
    };
  }

  /**
   * 收集屏幕信息
   */
  private collectScreenInfo(): ScreenInfo {
    const screen = window.screen;
    let orientation = "unknown";

    if (screen.orientation?.type) {
      orientation = screen.orientation.type;
    } else if (window.matchMedia) {
      orientation = window.matchMedia("(orientation: portrait)").matches
        ? "portrait"
        : "landscape";
    }

    return {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
      orientation,
    };
  }

  /**
   * 收集浏览器信息
   */
  private collectBrowserInfo(): BrowserInfo {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages || [navigator.language],
      platform: navigator.platform,
      vendor: navigator.vendor || "",
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || null,
    };
  }

  /**
   * 收集时区信息
   */
  private collectTimezoneInfo(): TimezoneInfo {
    const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
    return {
      id: resolvedOptions.timeZone,
      offset: new Date().getTimezoneOffset(),
      locale: resolvedOptions.locale,
    };
  }

  /**
   * 收集硬件信息
   */
  private collectHardwareInfo(): HardwareInfo {
    return {
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory:
        (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,
    };
  }

  /**
   * 收集 Canvas 指纹
   */
  private collectCanvasFingerprint(): CanvasFingerprint | null {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) return null;

      canvas.width = 200;
      canvas.height = 50;

      ctx.textBaseline = "top";
      ctx.font = "14px Arial, sans-serif";
      ctx.fillStyle = "#f60";
      ctx.fillRect(0, 0, 100, 50);

      ctx.fillStyle = "#069";
      ctx.fillText("Canvas Fingerprint", 2, 15);

      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillRect(50, 25, 100, 20);

      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, "red");
      gradient.addColorStop(0.5, "green");
      gradient.addColorStop(1, "blue");
      ctx.fillStyle = gradient;
      ctx.fillRect(100, 0, 50, 50);

      const dataURL = canvas.toDataURL();
      const hash = this.hashCode(dataURL);

      return {
        hash: hash.toString(36),
        length: dataURL.length,
      };
    } catch (error) {
      console.warn("Canvas指纹收集失败:", error);
      return null;
    }
  }

  /**
   * 收集 WebGL 指纹
   */
  private collectWebGLFingerprint(): WebGLFingerprint | null {
    try {
      const canvas = document.createElement("canvas");
      const gl =
        canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

      if (!gl) return null;

      const glContext = gl as WebGLRenderingContext;
      const debugInfo = glContext.getExtension("WEBGL_debug_renderer_info");

      const extensions = glContext.getSupportedExtensions() || [];

      return {
        renderer: debugInfo
          ? String(glContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL))
          : String(glContext.getParameter(glContext.RENDERER)),
        vendor: debugInfo
          ? String(glContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL))
          : String(glContext.getParameter(glContext.VENDOR)),
        version: String(glContext.getParameter(glContext.VERSION)),
        shadingLanguage: String(
          glContext.getParameter(glContext.SHADING_LANGUAGE_VERSION)
        ),
        extensionCount: extensions.length,
        topExtensions: extensions.slice(0, 3),
      };
    } catch (error) {
      console.warn("WebGL指纹收集失败:", error);
      return null;
    }
  }

  /**
   * 生成稳定的指纹 ID
   */
  private generateStableFingerprintId(): string {
    const data = {
      screen: `${window.screen.width}x${window.screen.height}`,
      pixelRatio: window.devicePixelRatio,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform,
      cores: navigator.hardwareConcurrency || 0,
    };

    const str = JSON.stringify(data);
    return this.hashCode(str).toString(36);
  }

  /**
   * 计算字符串哈希值
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  /**
   * 获取或创建会话 ID
   */
  private getOrCreateSessionId(): string {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId =
        "sess_" +
        Date.now() +
        "_" +
        Math.random().toString(36).substring(2, 11);
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * 获取完整的追踪数据
   */
  getTrackingData(): TrackingData {
    return {
      fingerprint: this.collectDeviceFingerprint(),
      session: {
        sessionId: this.sessionId,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
      },
    };
  }
}

// 懒加载单例实例
let trackerInstance: AttributionTracker | null = null;

/**
 * 获取归因追踪器实例（懒加载）
 */
export const getAttributionTracker = (): AttributionTracker => {
  if (!trackerInstance) {
    trackerInstance = new AttributionTracker();
  }
  return trackerInstance;
};

export default AttributionTracker;
